import {SaleVehiclePhotoType} from '@dms/api/settings';
import {
  SaleVehicleAlbumPhotoGuideLayoutItem,
  useArrangeSaleVehicleAlbumByPhotoGuideMutation,
  useAssignSaleVehiclePhotoTypeMutation,
  useUnassignSaleVehiclePhotoFromGuideMutation,
  useUpdateSaleVehicleAlbumPhotoGuideLayoutMutation,
} from '@dms/api/vehicle';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

interface UsePhotoGuideActionsProps {
  saleVehicleId: string;
  saleVehicleAlbumId: string;
  onSuccess?: () => void;
}

/** The Guide Layout mutations of one Sales Album, each reporting its result like the album actions. */
export const usePhotoGuideActions = (props: UsePhotoGuideActionsProps) => {
  const [assign] = useAssignSaleVehiclePhotoTypeMutation();
  const [unassign] = useUnassignSaleVehiclePhotoFromGuideMutation();
  const [updateLayout] = useUpdateSaleVehicleAlbumPhotoGuideLayoutMutation();
  const [arrange, {isLoading: isArranging}] = useArrangeSaleVehicleAlbumByPhotoGuideMutation();

  const albumArg = {
    saleVehicleId: props.saleVehicleId,
    saleVehicleAlbumId: props.saleVehicleAlbumId,
  };

  const handleSuccess = () => {
    props.onSuccess?.();
    return true;
  };
  // Every action resolves to whether it succeeded; a failure is reported by handleApiError.
  const handleFailure = (error: ApiError) => {
    handleApiError(error);
    return false;
  };

  return {
    assignPhotoType: (saleVehiclePhotoId: string, photoType: SaleVehiclePhotoType) =>
      assign({...albumArg, body: {saleVehiclePhotoId, photoType}})
        .unwrap()
        .then(handleSuccess)
        .catch(handleFailure),
    unassignPhoto: (saleVehiclePhotoId: string) =>
      unassign({...albumArg, body: {saleVehiclePhotoId}})
        .unwrap()
        .then(handleSuccess)
        .catch(handleFailure),
    updateLayout: (items: SaleVehicleAlbumPhotoGuideLayoutItem[]) =>
      updateLayout({...albumArg, body: {items}})
        .unwrap()
        .then(handleSuccess)
        .catch(handleFailure),
    arrangeByPhotoGuide: () => arrange(albumArg).unwrap().then(handleSuccess).catch(handleFailure),
    isArranging,
  };
};

export type PhotoGuideActions = ReturnType<typeof usePhotoGuideActions>;
